class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute("handle");
    this.swiperInitialized = false; // Prevent multiple initializations
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    // Prevent multiple initializations
    if (this.swiperInitialized) return;
    this.swiperInitialized = true;

    this.options = this.setOptions();
    this.instance = new Swiper('.swiper--' + this.handle, this.options);
    this.setInteractions();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", () => this.init());
      window.addEventListener("shopify:section:select", () => this.init());

      // fallback initialization after delay in design mode
      setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
    } else {
      window.addEventListener("load", () => this.init());
    }
  }

  disconnectedCallback() {
    if (this.instance && this.instance.destroy) this.instance.destroy();
  }

  /**
   * Options before initialization
   */
  setOptions() {
    if (this.debug) console.log('setOptions');
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    if (this.debug) console.log('getOptionsAsJsonScripts');
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);
    let options = {};
    if (jsonScriptAutomated && jsonScriptAutomated.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptAutomated.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptAutomated?.textContent);
      }
    }
    if (jsonScriptOverwrite && jsonScriptOverwrite.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptOverwrite.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptOverwrite?.textContent);
      }
    }
    return options;
  }

  /** thumbs instance should be mounted before this instance */
  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    return document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    if (this.debug) console.log('setInteractions');
  }
}
// customElements.define('instance-swiper', InstanceSwiper);
class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;
    if (this.debug) console.log('setCenteredSlides');

    const afterInit = (swiper) => {
      if (swiper.activeIndex !== 0) swiper.slideTo(0);
      if (this.debug) console.log('afterInit');
      const swiperWrapper = swiper.wrapperEl;
      const observer = new MutationObserver(resetSwiperWrapperTransform);
      observer.observe(swiperWrapper, { attributes: true });
      function resetSwiperWrapperTransform() {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
          if (this.debug) console.log('swiperWrapper observer removed styles!');
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (
              swiperWrapper.hasAttribute('style') &&
              swiperWrapper.style?.transform &&
              swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
            ) {
              observer.disconnect();
              if (this.debug) console.log('swiperWrapper observer disconnect!');
            }
          }, 500);
        }
      }

      /**
       * Quick fix for thumbnails dislocationing issue
       * when thumbnails are less than the height of the wrapper
       */
      const calculateAllThumbHeight = (this.querySelector(".swiper-slide").clientHeight + 16) * this.querySelectorAll(".swiper-slide").length;
      const thumbsWrapper = this.parentElement;
      function setThumbsWrapperHeightToAuto() {
        if (thumbsWrapper.offsetHeight > calculateAllThumbHeight) {
          thumbsWrapper.style.height = (calculateAllThumbHeight - 16) + "px";
        } else {
          thumbsWrapper.removeAttribute("style");
        }
      }
      window.addEventListener("resize", setThumbsWrapperHeightToAuto);
      // have to wait global.js media area height css variables to be generated
      setTimeout(() => {
        setThumbsWrapperHeightToAuto();
      }, 2500);
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit,
      },
    };
  }

}
if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}
class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute("thumbs");
    this.isZoomActive = this.hasAttribute("zoom");
    this.modelViewerBtn = this.querySelector('.model-viewer-btn');
    this.modelViewer = this.querySelector('model-viewer');
    this.isModelActive = false;
  }

  static get observedAttributes() {
    return ["thumbs", "zoom"];
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();
    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;
    if (this.debug) console.log("setThumbOptions");
    const thumbsInstance = super.getThumbsInstance();
    if (this.debug) console.log("thumbsInstance", thumbsInstance);
    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();
    this.initModelViewer();

    // Add thumb click handler for model slides
    const thumbSlides = document.querySelectorAll('.swiper-slide[data-media-type="model"]');
    thumbSlides.forEach(slide => {
      slide.addEventListener('click', () => this.enableSwiper());
    });

    // Force pagination update on mobile after each slide change
    this.instance.on("slideChange", swiper => {
      if (window.innerWidth < 750) {
        swiper.pagination.update();
        if (this.debug) console.log("Pagination updated manually on mobile.");
      }
    });

    // Detect scroll event and force update pagination when scrolling ends
    this.instance.on("scroll", swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        swiper.pagination.update();
        if (this.debug) console.log("Pagination updated after scroll.");
      }, 200);
    });
  }

  initModelViewer() {
    // Find all swiper slides with model viewers
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const container = slide.querySelector('.model-viewer-container');
      const modelViewer = container?.querySelector('model-viewer');

      if (btn && container && modelViewer) {
        // Store references to associated elements
        btn.modelViewer = modelViewer;
        modelViewer.controlButton = btn;

        // Model viewer container click handler
        container.addEventListener('click', (e) => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            btn.click();
          }
        });

        // Model viewer button click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleModelViewer(btn, modelViewer);
        });

        // Prevent model-viewer direct interaction
        modelViewer.addEventListener('mousedown', (e) => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });

    // Add navigation click handlers
    if (this.instance.navigation) {
      const { nextEl, prevEl } = this.instance.navigation;
      if (nextEl) {
        nextEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
      if (prevEl) {
        prevEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
    }
  }

  resetAllModelViewers() {
    // Find all model viewers in the current gallery instance
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const modelViewer = slide.querySelector('model-viewer');

      if (btn && modelViewer) {
        btn.classList.remove('is-active');
        modelViewer.interactionPrompt = 'none';
        modelViewer.cameraControls = false;
      }
    });

    this.isModelActive = false;
    this.instance.allowTouchMove = true;
  }

  toggleModelViewer(btn, modelViewer) {
    const wasActive = btn.classList.contains('is-active');

    // Reset all model viewers in this gallery
    this.resetAllModelViewers();

    if (!wasActive) {
      this.isModelActive = true;
      this.instance.allowTouchMove = false;

      // Enable only this specific model viewer
      modelViewer.interactionPrompt = 'auto';
      modelViewer.cameraControls = true;
      btn.classList.add('is-active');
    }
  }

  temporarilyDisableSwiper() {
    // Only disable touch/mouse dragging
    this.instance.allowTouchMove = false;

    // Re-enable after a short delay if no model interaction occurs
    this.swiperTimeout = setTimeout(() => {
      if (!this.isModelActive) {
        this.enableSwiper();
      }
    }, 100);
  }

  enableSwiper(force = false) {
    if (this.swiperTimeout) {
      clearTimeout(this.swiperTimeout);
    }

    // Always enable touch/mouse dragging if force is true (navigation/thumb clicks)
    if (force || !this.isModelActive) {
      this.instance.allowTouchMove = true;

      // Reset model viewer state if forced
      if (force && this.isModelActive) {
        this.isModelActive = false;
        if (this.modelViewer) {
          this.modelViewer.interactionPrompt = 'none';
          this.modelViewer.cameraControls = false;
        }
        this.modelViewerBtn.classList.remove('is-active');
      }
    }
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;
    if (this.debug) console.log("setThumbsInteraction");
    const thumbsSwiper = this.options.thumbs.swiper;

    // Enable swiper on thumb click
    thumbsSwiper.el.addEventListener('click', () => {
      this.enableSwiper(true);
      this.modelViewerBtn.classList.remove('btn--active');
    });

    this.instance.on("slideChangeTransitionStart", function (swiper) {
      const activeIndex = swiper.activeIndex;
      const thumbsActiveIndex = thumbsSwiper.activeIndex;
      if (this.debug) console.log("slideChangeTransitionStart", activeIndex, thumbsActiveIndex);
      if (activeIndex !== thumbsActiveIndex) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  // photoSwipeLightboxInit() {
  //   const [
  //     zoomContainerWidth,
  //     zoomContainerHeight,
  //     closeIcon,
  //     prevIcon,
  //     nextIcon
  //   ] = [
  //     this.offsetWidth,
  //     this.offsetHeight,
  //     document.querySelector("[data-close-icon]"),
  //     document.querySelector("[data-prev-icon]"),
  //     document.querySelector("[data-next-icon]")
  //   ];

  //   function isPhone() {
  //     return window.innerWidth < 750;
  //   }

  //   const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
  //     gallery: `.photoswipe-wrapper`,
  //     children: "a.product__gallery-toggle",
  //     mainClass: "pswp--product-media-gallery",

  //     loop: false,
  //     showHideAnimationType: "zoom",

  //     initialZoomLevel: zoomLevelObject => {
  //       if (isPhone()) {
  //         return zoomLevelObject.vFill;
  //       } else {
  //         return zoomLevelObject.fit;
  //       }
  //     },
  //     secondaryZoomLevel: zoomLevelObject => {
  //       if (isPhone()) {
  //         return zoomLevelObject.fit;
  //       } else {
  //         return 1;
  //       }
  //     },
  //     pswpModule: PhotoSwipe
  //   });
  //   photoSwipeLightboxInstance.addFilter(
  //     "uiElement",
  //     (element, data) => {
  //       if (data.name === "close") {
  //         element.innerHTML = closeIcon.innerHTML;
  //       } else if (data.name === "arrowPrev") {
  //         element.innerHTML = prevIcon.innerHTML;
  //       } else if (data.name === "arrowNext") {
  //         element.innerHTML = nextIcon.innerHTML;
  //       }
  //       return element;
  //     }
  //   );

  //   // html for video
  //   photoSwipeLightboxInstance.addFilter(
  //     "itemData",
  //     (itemData, index) => {
  //       if (itemData.type === "html" && itemData.element) {
  //         return {
  //           html: itemData.element.dataset.pswpHtml
  //         };
  //       }
  //       return itemData;
  //     }
  //   );

  //   photoSwipeLightboxInstance.init();

  //   photoSwipeLightboxInstance.on("beforeOpen", () => {
  //     document.body.classList.add("oveflow-hidden");
  //     const videos = this.querySelectorAll("video");
  //     Array.from(videos).forEach(video => {
  //       // if video is not playing, call playPromise
  //       // force to play
  //       video
  //         .play()
  //         .then(() => {
  //           // Automatic playback started!
  //           // Show playing UI.
  //           // video.play();
  //         })
  //         .catch(error => {
  //           // Auto-play was prevented
  //           // Show paused UI.
  //           video.pause();
  //         });
  //     });
  //   });

  //   photoSwipeLightboxInstance.on("closingAnimationStart", () => {
  //     document.body.classList.remove("oveflow-hidden");
  //   });
  // }

  // Gallary customisation customisation js
  photoSwipeLightboxInit() {
    const [
      zoomContainerWidth,
      zoomContainerHeight,
      closeIcon,
      prevIcon,
      nextIcon
    ] = [
      this.offsetWidth,
      this.offsetHeight,
      document.querySelector("[data-close-icon]"),
      document.querySelector("[data-prev-icon]"),
      document.querySelector("[data-next-icon]")
    ];

    function isPhone() {
      return window.innerWidth < 750;
    }

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: `.photoswipe-wrapper`,
      children: "a.product__gallery-toggle",
      mainClass: "pswp--product-media-gallery pswp--grid-gallery",
      loop: false,
      showHideAnimationType: "zoom",
      initialZoomLevel: zoomLevelObject => {
        if (isPhone()) {
          return zoomLevelObject.vFill;
        } else {
          return zoomLevelObject.fit;
        }
      },
      secondaryZoomLevel: zoomLevelObject => {
        if (isPhone()) {
          return zoomLevelObject.fit;
        } else {
          return 1;
        }
      },
      pswpModule: PhotoSwipe
    });

    // Filter UI elements - ENSURE CLOSE BUTTON IS ADDED
    photoSwipeLightboxInstance.addFilter(
      "uiElement",
      (element, data) => {
        if (data.name === "close") {
          element.innerHTML = closeIcon ? closeIcon.innerHTML : '✕';
          element.className = 'pswp__button pswp__button--close custom-close-btn';
        } else if (data.name === "arrowPrev") {
          element.innerHTML = prevIcon ? prevIcon.innerHTML : '←';
        } else if (data.name === "arrowNext") {
          element.innerHTML = nextIcon ? nextIcon.innerHTML : '→';
        }
        return element;
      }
    );

    // HTML for video
    photoSwipeLightboxInstance.addFilter(
      "itemData",
      (itemData, index) => {
        if (itemData.type === "html" && itemData.element) {
          return {
            html: itemData.element.dataset.pswpHtml
          };
        }
        return itemData;
      }
    );

    // ============================================
    // SEAMLESS INFINITE LOOP AUTO-SCROLL + DRAG + VIDEO SUPPORT
    // ============================================
    let animationFrameId = null;
    const SCROLL_SPEED = 0.5; // Smooth continuous speed
    let gridContainer = null;
    let gridWrapper = null;
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    let originalWidth = 0;

    // Replace default PhotoSwipe content with grid layout
    photoSwipeLightboxInstance.on("afterInit", () => {
      const pswp = photoSwipeLightboxInstance.pswp;
      
      // Hide default PhotoSwipe slides
      const pswpContainer = pswp.element.querySelector('.pswp__container');
      if (pswpContainer) {
        pswpContainer.style.display = 'none';
      }

      // Create grid layout
      createGridGallery(pswp);
      
      // Add drag functionality
      setupDragScroll();
      
      // Start continuous scroll - ALWAYS RUNNING
      // startContinuousScroll();

      // Force close button to be visible and clickable
      setTimeout(() => {
        ensureCloseButton(pswp);
      }, 50);
      
      // Double check after a bit
      setTimeout(() => {
        ensureCloseButton(pswp);
      }, 300);
    });

    function ensureCloseButton(pswp) {
      const closeBtn = pswp.element.querySelector('.pswp__button--close');
      if (closeBtn) {
        closeBtn.className = 'pswp__button pswp__button--close custom-close-btn';
        closeBtn.style.cssText = '';
        
        // Remove any existing click handlers and add new one
        const newCloseBtn = closeBtn.cloneNode(true);
        closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
        
        newCloseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          pswp.close();
        }, true);
        
        newCloseBtn.addEventListener('touchend', (e) => {
          e.preventDefault();
          e.stopPropagation();
          pswp.close();
        }, true);
      }
    }

    function waitForImagesToLoad(container) {
      const images = container.querySelectorAll('img');
      const promises = [];

      images.forEach(img => {
        if (!img.complete) {
          promises.push(
            new Promise(resolve => {
              img.onload = img.onerror = resolve;
            })
          );
        }
      });

      return Promise.all(promises);
    }

    function createGridGallery(pswp) {
      // Create main grid container
      gridContainer = document.createElement('div');
      gridContainer.className = 'pswp-multi-grid-container';
      
      // Get all slides data (images and videos)
      const slides = Array.from(document.querySelectorAll('.photoswipe-wrapper a.product__gallery-toggle'));
      
      // Create grid wrapper for images and videos
      gridWrapper = document.createElement('div');
      gridWrapper.className = 'pswp-multi-grid-wrapper';
      
      // Helper function to create grid item
      function createGridItem(slide, index) {
        const gridItem = document.createElement('div');
        gridItem.className = 'pswp-multi-grid-item';
        
        // Check if this is a video or image
        const isVideo = slide.dataset.pswpType === 'html' || slide.dataset.pswpHtml;
        
        if (isVideo && slide.dataset.pswpHtml) {
          // Create video element from HTML string
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = slide.dataset.pswpHtml;
          const videoElement = tempDiv.querySelector('video');
          
          if (videoElement) {
            videoElement.classList.add('pswp-grid-video');
            videoElement.setAttribute('playsinline', '');
            videoElement.setAttribute('loop', '');
            videoElement.setAttribute('muted', '');
            videoElement.setAttribute('autoplay', '');
            videoElement.style.width = '100%';
            videoElement.style.height = '100%';
            videoElement.style.objectFit = 'cover';
            
            gridItem.appendChild(videoElement);
            
            // Play video when it's in view
            setTimeout(() => {
              videoElement.play().catch(err => {
                console.log('Video autoplay prevented:', err);
              });
            }, 100);
          }
        } else {
          // Create image element
          const img = document.createElement('img');
          img.src = slide.href || slide.dataset.pswpSrc;
          img.alt = slide.dataset.pswpAlt || `Image ${index + 1}`;
          img.loading = 'eager';
          
          gridItem.appendChild(img);
        }
        
        return gridItem;
      }
      
      // Add original items (images and videos)
      slides.forEach((slide, index) => {
        const gridItem = createGridItem(slide, index);
        gridWrapper.appendChild(gridItem);
      });
      
      // Store original width for loop detection
      // setTimeout(() => {
      //   originalWidth = gridWrapper.offsetWidth;
        
      //   // Duplicate items for seamless loop (add 2 copies)
      //   for (let copy = 0; copy < 2; copy++) {
      //     slides.forEach((slide, index) => {
      //       const gridItem = createGridItem(slide, index);
      //       gridWrapper.appendChild(gridItem);
      //     });
      //   }
        
      //   // Play all videos
      //   setTimeout(() => {
      //     const allVideos = gridContainer.querySelectorAll('video');
      //     allVideos.forEach(video => {
      //       video.play().catch(err => {
      //         console.log('Video play error:', err);
      //       });
      //     });
      //   }, 200);
      // }, 100);
      
      gridContainer.appendChild(gridWrapper);

      // Wait until all images are fully loaded
      waitForImagesToLoad(gridWrapper).then(() => {
        // Calculate correct width AFTER images load
        originalWidth = gridWrapper.offsetWidth;

        // Duplicate items for seamless loop
        for (let copy = 0; copy < 2; copy++) {
          slides.forEach((slide, index) => {
            const gridItem = createGridItem(slide, index);
            gridWrapper.appendChild(gridItem);
          });
        }

        // Reset scroll position
        gridContainer.scrollLeft = 0;

        // Play videos
        const allVideos = gridContainer.querySelectorAll('video');
        allVideos.forEach(video => {
          video.play().catch(() => {});
        });

        // NOW start continuous scroll
        startContinuousScroll();
      });
      
      // Insert grid into PhotoSwipe
      const pswpElement = pswp.element;
      pswpElement.appendChild(gridContainer);
    }

    function startContinuousScroll() {
      stopContinuousScroll(); // Clear any existing animation
      
      function animate() {
        if (gridContainer && !isDragging) {
          const currentScroll = gridContainer.scrollLeft;
          
          // Scroll right to left (increasing scrollLeft)
          let newScroll = currentScroll + SCROLL_SPEED;
          
          // Seamless loop: when we reach the end of first set, reset to beginning
          if (originalWidth > 0 && newScroll >= originalWidth) {
            newScroll = newScroll - originalWidth;
            gridContainer.scrollLeft = newScroll;
          } else {
            gridContainer.scrollLeft = newScroll;
          }
          
          // Continue animation
          animationFrameId = requestAnimationFrame(animate);
        } else if (gridContainer && isDragging) {
          // Still check for loop even when dragging
          const currentScroll = gridContainer.scrollLeft;
          if (originalWidth > 0 && currentScroll >= originalWidth * 2) {
            gridContainer.scrollLeft = currentScroll - originalWidth;
          } else if (currentScroll <= 0) {
            gridContainer.scrollLeft = originalWidth;
          }
          animationFrameId = requestAnimationFrame(animate);
        }
      }
      
      animationFrameId = requestAnimationFrame(animate);
    }

    function stopContinuousScroll() {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    }

    function setupDragScroll() {
      // Mouse events
      gridContainer.addEventListener('mousedown', (e) => {
        isDragging = true;
        gridContainer.style.cursor = 'grabbing';
        startX = e.pageX - gridContainer.offsetLeft;
        scrollLeft = gridContainer.scrollLeft;
      });

      gridContainer.addEventListener('mouseleave', () => {
        isDragging = false;
        gridContainer.style.cursor = 'grab';
      });

      gridContainer.addEventListener('mouseup', () => {
        isDragging = false;
        gridContainer.style.cursor = 'grab';
      });

      gridContainer.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - gridContainer.offsetLeft;
        const walk = (x - startX) * 2; // Scroll speed multiplier
        gridContainer.scrollLeft = scrollLeft - walk;
      });

      // Touch events for mobile
      gridContainer.addEventListener('touchstart', (e) => {
        isDragging = true;
        startX = e.touches[0].pageX - gridContainer.offsetLeft;
        scrollLeft = gridContainer.scrollLeft;
      }, { passive: true });

      gridContainer.addEventListener('touchend', () => {
        isDragging = false;
      }, { passive: true });

      gridContainer.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        const x = e.touches[0].pageX - gridContainer.offsetLeft;
        const walk = (x - startX) * 2;
        gridContainer.scrollLeft = scrollLeft - walk;
      }, { passive: true });
    }

    photoSwipeLightboxInstance.init();

    photoSwipeLightboxInstance.on("beforeOpen", () => {
      document.body.classList.add("oveflow-hidden");
    });

    photoSwipeLightboxInstance.on("closingAnimationStart", () => {
      document.body.classList.remove("oveflow-hidden");
      stopContinuousScroll();
      
      // Pause all videos when closing
      if (gridContainer) {
        const allVideos = gridContainer.querySelectorAll('video');
        allVideos.forEach(video => {
          video.pause();
        });
      }
    });

    photoSwipeLightboxInstance.on("close", () => {
      stopContinuousScroll();
    });

    // Add CSS styles dynamically
    if (!document.getElementById('pswp-multi-grid-styles')) {
      const style = document.createElement('style');
      style.id = 'pswp-multi-grid-styles';
      style.textContent = `
        /* Hide default PhotoSwipe navigation */
        .pswp--grid-gallery .pswp__button--arrow {
          display: none !important;
        }
        
        .pswp--grid-gallery .pswp__counter {
          display: none !important;
        }
        
        /* Main grid container - fullscreen horizontal scroll */
        .pswp-multi-grid-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #f3f2ec;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 0;
          scroll-behavior: auto;
          -webkit-overflow-scrolling: touch;
          cursor: grab;
          user-select: none;
        }
        
        .pswp-multi-grid-container:active {
          cursor: grabbing;
        }
        
        /* Hide scrollbar but keep functionality */
        .pswp-multi-grid-container::-webkit-scrollbar {
          height: 0px;
          background: transparent;
        }
        
        .pswp-multi-grid-container {
          scrollbar-width: none;
        }
        
        /* Grid wrapper - horizontal layout with NO gaps */
        .pswp-multi-grid-wrapper {
          display: flex;
          flex-direction: row;
          gap: 0;
          height: 100vh;
          width: max-content;
        }
        
        /* Individual grid items - seamless, no gaps */
        .pswp-multi-grid-item {
          position: relative;
          flex-shrink: 0;
          width: 30vw;
          height: calc(100vh - 40px);
          overflow: hidden;
          // background: #1a1a1a;
          margin: 20px 10px;
        }
        
        .pswp-multi-grid-item img {
          width: 100%;
          height: auto;
          object-fit: cover;
          display: block;
          transition: transform 0.3s ease;
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
        }
        // .pswp-multi-grid-item video {
        //     width: 100% !important;
        //     height: auto !important;
        // }
        // .pswp-multi-grid-item:hover img,
        // .pswp-multi-grid-item:hover video {
        //   transform: scale(1.05);
        // }
        
        /* Video specific styles */
        .pswp-grid-video {
          pointer-events: auto !important;
        }
        
        /* Mobile adjustments */
        @media (max-width: 1199px) {
          .pswp-multi-grid-item {
            width: 50vw;
          }
        }
        @media (max-width: 750px) {
          .pswp-multi-grid-item {
            width: 100vw;
          }
        }
        
        /* CLOSE BUTTON - MAXIMUM VISIBILITY */
        .pswp--grid-gallery .custom-close-btn,
        .pswp--grid-gallery .pswp__button--close {
          position: fixed !important;
          top: 20px !important;
          right: 20px !important;
          z-index: 999999 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 60px !important;
          height: 60px !important;
          border-radius: 50% !important;
          background: rgba(0, 0, 0, 0.8) !important;
          color: white !important;
          font-size: 40px !important;
          font-weight: bold !important;
          cursor: pointer !important;
          border: 3px solid rgba(255, 255, 255, 0.8) !important;
          opacity: 1 !important;
          visibility: visible !important;
          transition: all 0.3s ease !important;
          pointer-events: auto !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.8) !important;
          line-height: 1 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        
        .pswp--grid-gallery .custom-close-btn:hover,
        .pswp--grid-gallery .pswp__button--close:hover {
          background: rgba(255, 255, 255, 0.2) !important;
          transform: scale(1.15) rotate(90deg) !important;
        }
        
        .pswp--grid-gallery .custom-close-btn svg,
        .pswp--grid-gallery .pswp__button--close svg,
        .pswp--grid-gallery .custom-close-btn *,
        .pswp--grid-gallery .pswp__button--close * {
          fill: white !important;
          color: white !important;
          stroke: white !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(
      this.querySelectorAll("[data-media-id]")
    ).find(media => Number(media.dataset.mediaId) === id);

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }
}
if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}


class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();
    this.init();
    window.addEventListener("resize", this.init.bind(this));
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", this.init.bind(this));
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider').offsetWidth || 330;
    if (document.querySelector('.main-product__media--grid-item') && window.innerWidth > 750) {
      containerOffsetWidth = document.querySelector('.main-product__media--grid-item').offsetWidth;
    }
    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const mediaInfoTextWidth = this.querySelector('p:last-child').offsetWidth;
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${mediaInfoTextWidth / maxWidthForInfo * 8}s`;
      mediaInfoHidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      mediaInfoHidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}